import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanetaryUpdatesCard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [notifications, setNotifications] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const planetaryEvents = [
    {
      id: 1,
      type: 'transit',
      planet: 'Mercury',
      event: 'Entering Retrograde',
      impact: 'Communication challenges ahead',
      time: '2:30 PM',
      severity: 'high',
      icon: 'AlertTriangle'
    },
    {
      id: 2,
      type: 'aspect',
      planet: 'Venus',
      event: 'Conjunct Mars',
      impact: 'Heightened romantic energy',
      time: '6:45 PM',
      severity: 'medium',
      icon: 'Heart'
    },
    {
      id: 3,
      type: 'lunar',
      planet: 'Moon',
      event: 'Entering Pisces',
      impact: 'Intuitive and emotional period',
      time: '11:20 PM',
      severity: 'low',
      icon: 'Moon'
    }
  ];

  const currentPlanetaryData = [
    {
      planet: 'Sun',
      sign: '♌',
      degree: '15°32\'',
      house: '5th',
      icon: 'Sun',
      color: '#F59E0B'
    },
    {
      planet: 'Moon',
      sign: '♓',
      degree: '28°14\'',
      house: '12th',
      icon: 'Moon',
      color: '#8B5CF6'
    },
    {
      planet: 'Mercury',
      sign: '♍',
      degree: '3°45\'',
      house: '6th',
      icon: 'Zap',
      color: '#10B981',
      retrograde: true
    },
    {
      planet: 'Venus',
      sign: '♋',
      degree: '21°08\'',
      house: '4th',
      icon: 'Heart',
      color: '#EF4444'
    },
    {
      planet: 'Mars',
      sign: '♋',
      degree: '19°52\'',
      house: '4th',
      icon: 'Flame',
      color: '#EF4444'
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-accent';
      case 'low':
        return 'text-success';
      default:
        return 'text-text-secondary';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-error/20';
      case 'medium':
        return 'bg-accent/20';
      case 'low':
        return 'bg-success/20';
      default:
        return 'bg-surface/50';
    }
  };

  return (
    <div className="glass bg-surface/50 backdrop-blur-cosmic rounded-2xl p-6 shadow-elevation-3">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-text-primary">
            Planetary Updates
          </h2>
          <p className="text-sm text-text-secondary font-mono mt-1">
            {currentTime.toLocaleTimeString('en-US', { 
              hour: '2-digit', 
              minute: '2-digit',
              timeZoneName: 'short'
            })}
          </p>
        </div>
        <Button
          variant="ghost"
          iconName={notifications ? "Bell" : "BellOff"}
          onClick={() => setNotifications(!notifications)}
          className={notifications ? 'text-primary' : 'text-text-secondary'}
        />
      </div>

      {/* Current Planetary Positions */}
      <div className="mb-6">
        <h3 className="text-sm font-heading font-medium text-text-primary mb-3">
          Current Positions
        </h3>
        <div className="grid grid-cols-1 gap-2">
          {currentPlanetaryData.map((planet) => (
            <div
              key={planet.planet}
              className="flex items-center justify-between p-3 glass bg-surface/30 backdrop-blur-sm rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <Icon name={planet.icon} size={16} color={planet.color} />
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-text-primary">
                      {planet.planet}
                    </span>
                    {planet.retrograde && (
                      <span className="text-xs bg-error/20 text-error px-1 py-0.5 rounded">
                        Rx
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-text-secondary font-mono">
                    {planet.house} House
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-mono text-text-primary">
                  {planet.sign} {planet.degree}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Events */}
      <div className="mb-6">
        <h3 className="text-sm font-heading font-medium text-text-primary mb-3">
          Today's Events
        </h3>
        <div className="space-y-3">
          {planetaryEvents.map((event) => (
            <div
              key={event.id}
              className={`p-3 rounded-lg border-l-4 ${
                event.severity === 'high' ? 'border-error' :
                event.severity === 'medium'? 'border-accent' : 'border-success'
              } ${getSeverityBg(event.severity)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <Icon name={event.icon} size={16} color="currentColor" />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-text-primary">
                        {event.planet}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {event.event}
                      </span>
                    </div>
                    <p className="text-xs text-text-secondary mt-1">
                      {event.impact}
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-mono ${getSeverityColor(event.severity)}`}>
                  {event.time}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          iconName="Calendar"
          onClick={() => console.log('View full calendar')}
          fullWidth
        >
          Full Calendar
        </Button>
        <Button
          variant="outline"
          iconName="Settings"
          onClick={() => console.log('Notification settings')}
          fullWidth
        >
          Settings
        </Button>
      </div>
    </div>
  );
};

export default PlanetaryUpdatesCard;